import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CollegePage } from '../../models/college-dashboard.model';
import { CustomSelectComponent } from '../shared/custom-select/custom-select.component';
import { buildYearDropdownOptions } from '../../utils/academic-year.util';

@Component({
  selector: 'app-college-header',
  standalone: true,
  imports: [CommonModule, CustomSelectComponent],
  templateUrl: './college-header.component.html',
  styleUrl: './college-header.component.scss'
})
export class CollegeHeaderComponent {
  @Input() activePage: CollegePage = 'Overview';
  @Input() academicYear: string = 'All Years';
  @Input() department: string = 'All Departments';
  @Input() institutionName: string = 'P.K. College of Engineering';
  @Input() placementOfficer: string = 'Placement Officer';
  @Input() collegeLogoUrl: string | null = null;
  // Fed by the parent layout, which already fetches the college profile for
  // institutionName/placementOfficer/collegeLogoUrl — avoids a second,
  // duplicate GET /me/profile call just for this dropdown's options.
  @Input() deptOptions: string[] = ['All Departments'];
  // Distinct year_of_passing values actually present among the college's
  // students (GET /me/students/years), fetched once by the parent layout.
  // These ARE the dropdown's options — no padding with "nearby" years that
  // would return zero results if picked (e.g. a college with students only
  // in 2010 and 2050 shows exactly those two, nothing in between).
  @Input() availableYears: number[] = [];

  @Output() yearChange = new EventEmitter<string>();
  @Output() departmentChange = new EventEmitter<string>();
  @Output() openProfile = new EventEmitter<void>();
  @Output() signOut = new EventEmitter<void>();

  profileMenuOpen: boolean = false;

  constructor(private elementRef: ElementRef) {}

  /**
   * Plain graduation years (the exact values stored in User.year_of_passing),
   * not academic-year ranges — a "2025–26" style range previously got
   * translated to year_of_passing 2026 behind the scenes (parseYearOfPassing's
   * old +1 rule), which meant the option whose label matched a student's
   * actual year_of_passing never selected them. Showing the real year
   * directly removes that hidden translation entirely: selecting "2026"
   * filters for year_of_passing === 2026, full stop.
   *
   * A getter (not a field computed once at construction) so it stays in
   * sync once availableYears arrives asynchronously from the parent's API
   * call, without needing an OnChanges hook. Before that call resolves (or
   * for a brand-new college with zero students), this is just ['All Years']
   * — intentionally not padded with guessed years, so every option shown
   * always returns real students when selected.
   */
  get yearOptions(): string[] {
    return buildYearDropdownOptions(this.availableYears);
  }

  get subtitle(): string {
    if (this.activePage !== 'Overview') {
      return 'Manage your campus placement operations';
    }
    return this.academicYear === 'All Years'
      ? 'Placement overview across all academic years'
      : `Placement overview for the ${this.academicYear} batch`;
  }

  get initials(): string {
    return (
      this.placementOfficer
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase() || 'PO'
    );
  }

  toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.profileMenuOpen = !this.profileMenuOpen;
  }

  onProfileClick(event: MouseEvent): void {
    event.stopPropagation();
    this.profileMenuOpen = false;
    this.openProfile.emit();
  }

  onSignOutClick(event: MouseEvent): void {
    event.stopPropagation();
    this.profileMenuOpen = false;
    this.signOut.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.profileMenuOpen = false;
    }
  }
}
